/**
 * DocumentSignedReturn — DocuSign embedded-signing return landing (negotiation /
 * purchase-agreement flow).
 *
 * DocuSign redirects the signer's browser here the instant they finish (or
 * decline / cancel) the embedded ceremony for a two-party document:
 *
 *     /documents/:id/signed?event=<advisory>
 *
 * The `event` query param DocuSign appends (signing_complete / decline / cancel /
 * ttl_expired / …) is ADVISORY only. The authoritative state comes from the
 * envelope itself, so on mount we call `POST /api/documents/:id/reconcile` — which
 * reads the live DocuSign status and finalizes the whole bundle server-side —
 * and reflect the real signer/envelope status. This is the synchronous
 * counterpart to the durable DocuSign Connect webhook: if a callback is dropped
 * (or rejected in an env whose Connect HMAC key is unset), reconcile still flips
 * a fully-signed envelope to completed instead of leaving it stuck at "sent". The
 * call is idempotent with the webhook, so we can briefly poll it while the other
 * party finishes.
 *
 * Routing: the cutthe6.com frontend has no React Router — App.js inspects
 * window.location.pathname and renders this (inside the auth gate, since the
 * fetch needs the authenticated signer) when the path matches /documents/:id/signed.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import DocumentAPI from '../../agents/documentAgent';
import './DocumentSignedReturn.css';

const POLL_INTERVAL_MS = 5000;
const MAX_AUTO_POLLS = 6; // ~30s of background polling while awaiting completion

// DocuSign "event" values that mean the signer did NOT complete signing.
const NEGATIVE_EVENTS = new Set([
  'decline',
  'cancel',
  'ttl_expired',
  'session_timeout',
  'exception',
  'id_check_failed',
]);

/** Parse the document id (path) and advisory event (query). Exported for tests. */
export function parseSignedReturnParams(
  pathname = typeof window !== 'undefined' ? window.location.pathname : '',
  search = typeof window !== 'undefined' ? window.location.search : '',
) {
  const match = /\/documents\/(\d+)\/signed/.exec(pathname || '');
  const documentId = match ? Number(match[1]) : null;
  const params = new URLSearchParams(search || '');
  const event = params.get('event') || null;
  return { documentId, event };
}

/**
 * @param {object} props
 * @param {number} [props.documentId] - override (defaults to URL path segment)
 * @param {number} [props.currentUserId] - the signed-in user, to locate their signature
 * @param {() => void} [props.onDone] - return the signer to the app (falls back to `/`)
 */
export default function DocumentSignedReturn({
  documentId: documentIdProp,
  currentUserId,
  onDone,
} = {}) {
  const parsed = useRef(parseSignedReturnParams()).current;
  const documentId = documentIdProp ?? parsed.documentId;
  const event = parsed.event;

  // 'loading' | 'complete' | 'signed' | 'declined' | 'pending' | 'error'
  const [phase, setPhase] = useState('loading');
  const [doc, setDoc] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [pollCount, setPollCount] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const headingRef = useRef(null);
  const cancelledRef = useRef(false);

  const applyDoc = useCallback((payload) => {
    setDoc(payload);
    const mySig = Array.isArray(payload?.signatures)
      ? payload.signatures.find((s) => s.signer_id === currentUserId)
      : null;

    if (payload?.status === 'completed' || payload?.envelope_status === 'completed') {
      setPhase('complete');
    } else if (mySig?.status === 'declined' || NEGATIVE_EVENTS.has(event)) {
      setPhase('declined');
    } else if (mySig?.status === 'signed' || event === 'signing_complete') {
      // Your signature is recorded; the envelope is still awaiting the other party.
      setPhase('signed');
    } else {
      setPhase('pending');
    }
  }, [currentUserId, event]);

  const runFetch = useCallback(async () => {
    if (!documentId) {
      setPhase('error');
      setErrorMessage('We could not determine which document this signature belongs to.');
      return;
    }
    try {
      // Reconcile (not a plain GET): pulls live envelope status and finalizes
      // server-side, so completion shows even when the webhook never landed.
      const payload = await DocumentAPI.reconcileDocument(documentId);
      if (cancelledRef.current) return;
      applyDoc(payload);
    } catch (err) {
      if (cancelledRef.current) return;
      console.error('Document signed-return reconcile failed:', err);
      setPhase('error');
      setErrorMessage(err?.message || "We couldn't confirm your signature. Please try again.");
    }
  }, [documentId, applyDoc]);

  useEffect(() => {
    cancelledRef.current = false;
    runFetch();
    return () => { cancelledRef.current = true; };
  }, [runFetch]);

  // Short auto-poll while the envelope is not yet fully executed, so a webhook
  // that lands a moment after the redirect flips us to the "complete" state.
  useEffect(() => {
    if (phase !== 'signed' && phase !== 'pending') return undefined;
    if (pollCount >= MAX_AUTO_POLLS) return undefined;
    const id = setTimeout(() => {
      setPollCount((c) => c + 1);
      runFetch();
    }, POLL_INTERVAL_MS);
    return () => clearTimeout(id);
  }, [phase, pollCount, runFetch]);

  useEffect(() => {
    if (['complete', 'signed', 'declined', 'error'].includes(phase) && headingRef.current) {
      headingRef.current.focus();
    }
  }, [phase]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await runFetch();
    setRefreshing(false);
  };

  const handleReturn = () => {
    if (typeof onDone === 'function') {
      onDone();
      return;
    }
    window.location.assign('/');
  };

  const title = doc?.title || 'document';

  return (
    <div className="doc-signed-return" data-testid="document-signed-return">
      <div className="doc-signed-return__card" role="status" aria-live="polite" aria-atomic="true">
        {phase === 'loading' && (
          <div data-testid="document-signed-return-loading">
            <span className="doc-signed-return__spinner" aria-hidden="true" />
            <h1 className="doc-signed-return__title">Confirming your signature…</h1>
            <p className="doc-signed-return__subtitle">
              Just a moment while we finalize this with DocuSign.
            </p>
          </div>
        )}

        {phase === 'complete' && (
          <div data-testid="document-signed-return-complete">
            <h1 className="doc-signed-return__title doc-signed-return__title--success" ref={headingRef} tabIndex={-1}>
              <span className="doc-signed-return__icon" aria-hidden="true">✓</span>
              Document fully executed
            </h1>
            <p className="doc-signed-return__subtitle">
              All parties have signed the {title}. You can safely close this tab.
            </p>
            <div className="doc-signed-return__actions">
              <button type="button" className="doc-signed-return__btn doc-signed-return__btn--primary" onClick={handleReturn} data-testid="document-signed-return-back">
                Return to your documents
              </button>
            </div>
          </div>
        )}

        {phase === 'signed' && (
          <div data-testid="document-signed-return-signed">
            <h1 className="doc-signed-return__title doc-signed-return__title--success" ref={headingRef} tabIndex={-1}>
              <span className="doc-signed-return__icon" aria-hidden="true">✓</span>
              Your signature has been recorded
            </h1>
            <p className="doc-signed-return__subtitle">
              Thanks — your part of the {title} is signed. We&apos;re waiting on the other
              party to finish. You can safely close this tab; the document updates
              automatically once everyone has signed.
            </p>
            <div className="doc-signed-return__actions">
              <button type="button" className="doc-signed-return__btn doc-signed-return__btn--primary" onClick={handleReturn} data-testid="document-signed-return-back">
                Return to your documents
              </button>
              <button type="button" className="doc-signed-return__btn doc-signed-return__btn--ghost" onClick={handleRefresh} disabled={refreshing} data-testid="document-signed-return-refresh">
                {refreshing ? 'Checking…' : 'Refresh status'}
              </button>
            </div>
          </div>
        )}

        {phase === 'pending' && (
          <div data-testid="document-signed-return-pending">
            <h1 className="doc-signed-return__title">
              <span className="doc-signed-return__spinner doc-signed-return__spinner--inline" aria-hidden="true" />
              Finalizing your signature…
            </h1>
            <p className="doc-signed-return__subtitle">
              DocuSign is still recording your signature on the {title}. This usually
              takes only a few seconds — we&apos;ll keep checking automatically.
            </p>
            <div className="doc-signed-return__actions">
              <button type="button" className="doc-signed-return__btn doc-signed-return__btn--primary" onClick={handleRefresh} disabled={refreshing} data-testid="document-signed-return-refresh">
                {refreshing ? 'Checking…' : 'Refresh status'}
              </button>
              <button type="button" className="doc-signed-return__btn doc-signed-return__btn--ghost" onClick={handleReturn} data-testid="document-signed-return-back">
                Return to your documents
              </button>
            </div>
          </div>
        )}

        {phase === 'declined' && (
          <div data-testid="document-signed-return-declined">
            <h1 className="doc-signed-return__title doc-signed-return__title--error" ref={headingRef} tabIndex={-1}>
              <span className="doc-signed-return__icon" aria-hidden="true">⚠</span>
              Signing was not completed
            </h1>
            <p className="doc-signed-return__subtitle">
              The signing session was declined or cancelled, so nothing was signed on
              the {title}. You can start signing again from your documents whenever
              you&apos;re ready.
            </p>
            <div className="doc-signed-return__actions">
              <button type="button" className="doc-signed-return__btn doc-signed-return__btn--primary" onClick={handleReturn} data-testid="document-signed-return-back">
                Return to your documents
              </button>
            </div>
          </div>
        )}

        {phase === 'error' && (
          <div data-testid="document-signed-return-error">
            <h1 className="doc-signed-return__title doc-signed-return__title--error" ref={headingRef} tabIndex={-1}>
              <span className="doc-signed-return__icon" aria-hidden="true">⚠</span>
              Something went wrong
            </h1>
            <p className="doc-signed-return__subtitle" role="alert">{errorMessage}</p>
            <div className="doc-signed-return__actions">
              <button type="button" className="doc-signed-return__btn doc-signed-return__btn--primary" onClick={handleRefresh} disabled={refreshing} data-testid="document-signed-return-retry">
                {refreshing ? 'Retrying…' : 'Try again'}
              </button>
              <button type="button" className="doc-signed-return__btn doc-signed-return__btn--ghost" onClick={handleReturn} data-testid="document-signed-return-back">
                Return to your documents
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
